import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas, qaSchema, SITE_URL } from "@/lib/seo";

const PATH = "/fragen/welcher-shopify-tarif";
const TITLE = "Welcher Shopify-Tarif ist der richtige?";
const DESCRIPTION =
  "Basic, Grow, Advanced oder Plus: Wie Sie den passenden Shopify-Tarif am Bestellvolumen, an den Zahlungsgebühren und am Funktionsbedarf festmachen.";
const UPDATED = "2026-09-10";
const ANSWER =
  "Starten Sie im kleinsten Tarif, der Ihre Funktionen abdeckt, und wechseln Sie erst, wenn die Rechnung dafür spricht. Der Sprung in einen höheren Tarif lohnt sich, sobald die eingesparten Zahlungsgebühren die höhere Grundgebühr übersteigen oder Sie eine Funktion zwingend brauchen. Prüfen Sie das mit Ihren echten Bestellzahlen der letzten drei Monate, nicht mit einer Prognose.";

const faqs = [
  {
    q: "Welcher Shopify-Tarif ist für den Start sinnvoll?",
    a: "Der kleinste Tarif, der Ihre benötigten Funktionen abdeckt. Solange Bestellvolumen und Anforderungen überschaubar sind, zahlt ein höherer Tarif sich nicht aus.",
  },
  {
    q: "Wann lohnt sich der Wechsel in einen höheren Tarif?",
    a: "Wenn die Ersparnis bei den Zahlungsgebühren die höhere Grundgebühr übersteigt, oder wenn Sie eine Funktion brauchen, die der aktuelle Tarif nicht bietet. Rechnen Sie mit Ihren tatsächlichen Bestellzahlen.",
  },
  {
    q: "Spart die Jahreszahlung wirklich Geld?",
    a: "Ja, Shopify gewährt bei jährlicher Zahlung einen Rabatt auf die Grundgebühr. Sinnvoll ist das, wenn der Shop bereits läuft und Sie die Bindung sicher nutzen.",
  },
  {
    q: "Beeinflusst der Tarif meine Conversion Rate?",
    a: "Nur indirekt. Der Tarif bestimmt Gebühren und verfügbare Funktionen. Ob Besucher kaufen, entscheiden Produktseiten, Ladezeit, Versandinfos und der Kaufabschluss.",
  },
  {
    q: "Kann ich den Tarif später wechseln?",
    a: "Ja, ein Wechsel ist jederzeit möglich und ändert nichts an Ihren Daten, Produkten oder Bestellungen.",
  },
];

export const Route = createFileRoute("/fragen/welcher-shopify-tarif")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
    scripts: [
      ...articleSchemas({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        faqs,
        section: "Shopify Tarife",
        updated: UPDATED,
        breadcrumb: { name: "Fragen", path: "/fragen" },
      }),
      qaSchema({ question: TITLE, answer: ANSWER, path: PATH, updated: UPDATED }),
    ],
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Frage · Tarife"
      title="Welcher Shopify-Tarif ist der richtige?"
      intro="Kurz gesagt: der kleinste Tarif, der Ihre Funktionen abdeckt. Der Wechsel nach oben rechnet sich erst, wenn Zahlungsgebühren oder eine fehlende Funktion es verlangen."
      source="frage-shopify-tarif"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/fragen", label: "Fragen" }}
      sections={[
        {
          h: "Woraus sich die Tarifkosten zusammensetzen",
          p: [
            "Jeder Tarif besteht aus zwei Teilen: einer monatlichen Grundgebühr und einem Prozentsatz plus Festbetrag je Zahlung. Höhere Tarife senken den Prozentsatz und heben die Grundgebühr an.",
            "Deshalb hängt die richtige Wahl fast ausschließlich am Bestellvolumen. Wer wenige Bestellungen hat, zahlt im großen Tarif drauf. Wer viele hat, spart dort spürbar.",
          ],
          list: [
            "Monatliche Grundgebühr, günstiger bei jährlicher Zahlung",
            "Zahlungsgebühr je Bestellung, sinkt mit dem Tarif",
            "Zusätzliche Gebühr bei externen Zahlungsanbietern",
            "Apps und Domain, unabhängig vom Tarif",
          ],
        },
        {
          h: "So rechnen Sie es selbst nach",
          p: [
            "Nehmen Sie Bestellungen und Umsatz der letzten drei Monate. Rechnen Sie die Zahlungsgebühren einmal mit dem aktuellen und einmal mit dem nächsthöheren Satz und addieren Sie jeweils die Grundgebühr. Die kleinere Summe gewinnt.",
            "Diese Rechnung schlägt jede Empfehlung, weil sie mit Ihren Zahlen arbeitet und nicht mit einem Durchschnitt.",
          ],
        },
        {
          h: "Wann der Funktionsbedarf entscheidet",
          p: [
            "Manchmal ist die Rechnung nebensächlich, weil eine Funktion fehlt. Typische Auslöser sind mehrere Länder oder Sprachen, B2B-Preise und Kundengruppen oder Eingriffe in den Kaufabschluss, die der Standardtarif nicht zulässt.",
          ],
          list: [
            "Mehrere Shops, Marken oder Märkte parallel",
            "B2B mit Preislisten und Nettopreisen",
            "Tiefere Anpassungen am Kaufabschluss",
            "Automatisierung wiederkehrender Abläufe",
          ],
        },
        {
          h: "Der häufigste Fehler",
          p: [
            "Ein größerer Tarif wird gebucht, um Umsatzprobleme zu lösen. Das funktioniert nicht: Tarife ändern Gebühren und Funktionen, nicht die Kaufbereitschaft. Wenn der Umsatz stockt, liegt der Hebel bei Produktseiten, Ladezeit, Versandinformationen und dem Kaufabschluss.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/fragen/was-kostet-ein-shopify-shop", label: "Was kostet ein Shopify-Shop?" },
        { to: "/fragen/wann-lohnt-sich-shopify-plus", label: "Wann lohnt sich Shopify Plus?" },
        { to: "/ratgeber/shopify-kosten", label: "Shopify Kosten im Detail" },
      ]}
    />
  );
}
